import {differenceInMinutes, parseISO} from 'date-fns';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = MINUTES_PER_HOUR * 24;

type TimeToFirstResponse = {
  timeToFirstResponse?: string;
  days?: number | null;
  hours?: number | null;
  minutes?: number | null;
  hasEnded?: boolean;
};

export const getTimeToFirstResponse = <T extends {createdAt?: string | Nullish}>(
  data: T | Nullish,
  firstResponseAt: string | Nullish,
  ttfrBusinessMinutes?: number | Nullish
): TimeToFirstResponse => {
  if (isNil(data)) {
    return {};
  }
  const now = new Date();

  const createdDate = data?.createdAt ? parseISO(data.createdAt) : null;

  const diffInMinutes = match({ttfrBusinessMinutes, createdDate})
    .with({ttfrBusinessMinutes: Pattern.number}, ({ttfrBusinessMinutes: value}) => value)
    .with({createdDate: Pattern.not(Pattern.nullish)}, ({createdDate: created}) =>
      differenceInMinutes(firstResponseAt ?? now, created)
    )
    .otherwise(() => null);

  const days = isNotNil(diffInMinutes) ? Math.floor(diffInMinutes / MINUTES_PER_DAY) : null;
  const hours = isNotNil(diffInMinutes)
    ? Math.floor((diffInMinutes % MINUTES_PER_DAY) / MINUTES_PER_HOUR)
    : null;
  const minutes = isNotNil(diffInMinutes) ? diffInMinutes % MINUTES_PER_HOUR : null;

  const timeToFirstResponse = [
    // we don't want to show 0 values, but 0m is ok
    days ? days + i18n.t('general.labels.dayCompact') : null,
    hours ? hours + i18n.t('general.labels.hourCompact') : null,
    isNotNilOrEmpty(minutes) ? minutes! + i18n.t('general.labels.minuteCompact') : null,
  ]
    .filter(Boolean)
    .join(' ');

  return {timeToFirstResponse, days, hours, minutes, hasEnded: isNotNilOrEmpty(firstResponseAt)};
};
